import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, MapPin, Calendar, Building2, X, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { Logo } from "../components/site/Logo";
import { EXECUTED_PROJECTS } from "../data/hseData";

export default function PortfolioPage() {
  const [selected, setSelected] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [filter, setFilter] = useState("Todos");

  // Aggregate all tags for filter
  const allTags = ["Todos", ...Array.from(new Set(EXECUTED_PROJECTS.flatMap((p) => p.tags)))];

  const filtered =
    filter === "Todos"
      ? EXECUTED_PROJECTS
      : EXECUTED_PROJECTS.filter((p) => p.tags.includes(filter));

  useEffect(() => {
    if (selected) setPhotoIdx(0);
  }, [selected]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (!selected) return;
      if (e.key === "Escape") setSelected(null);
      if (e.key === "ArrowRight") setPhotoIdx((i) => Math.min(i + 1, selected.images.length - 1));
      if (e.key === "ArrowLeft") setPhotoIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <div className="min-h-screen bg-[#0B1B2D] text-white">
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Logo size="md" />
          <a
            href="/"
            data-testid="portfolio-back"
            className="inline-flex items-center gap-2 font-condensed uppercase text-xs tracking-[0.2em] font-bold text-white/70 hover:text-white"
          >
            <ArrowLeft size={14} /> Voltar ao site
          </a>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
        <div className="mb-10">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#2FA36B]" />
            <span className="font-condensed uppercase text-xs tracking-[0.4em] text-[#2FA36B] font-bold">Portfólio HSE</span>
          </div>
          <h1 className="font-condensed font-bold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
            Projetos entregues em
            <span className="text-[#2FA36B]"> operações críticas</span>.
          </h1>
          <p className="mt-6 text-white/65 font-body text-base max-w-3xl leading-relaxed">
            Amostra de projetos executados pela HSE em diferentes segmentos industriais e comerciais. Cada card exibe imagens reais do trabalho realizado em campo.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {allTags.map((t) => (
            <button
              key={t}
              data-testid={`portfolio-filter-${t}`}
              onClick={() => setFilter(t)}
              className={`font-condensed uppercase text-[11px] tracking-[0.25em] font-bold px-4 py-2.5 border transition-colors ${
                filter === t
                  ? "bg-[#2FA36B] border-[#2FA36B] text-white"
                  : "bg-transparent border-white/15 text-white/70 hover:border-white/50 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.07] border border-white/[0.07]">
          {filtered.map((p, i) => (
            <motion.button
              key={`${p.client}-${p.year}-${p.location}`}
              data-testid={`portfolio-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
              onClick={() => setSelected(p)}
              className="text-left bg-[#12283F] hover:bg-[#173250] transition-colors group focus:outline-none focus:ring-2 focus:ring-[#2FA36B]"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0B1B2D]">
                <img
                  src={p.images[0]}
                  alt={p.client}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute top-4 right-4 bg-[#2FA36B] text-white font-condensed font-bold uppercase text-[11px] tracking-[0.2em] px-2.5 py-1">
                  {p.year}
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-[#2FA36B] font-bold">
                    {p.location}
                  </p>
                  <p className="font-condensed font-bold uppercase text-lg text-white leading-tight tracking-tight mt-1">
                    {p.client}
                  </p>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-condensed font-bold uppercase text-base text-white leading-tight tracking-tight mb-3 min-h-[3rem]">
                  {p.title}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {p.tags.slice(0, 3).map((t) => (
                    <span key={t} className="font-condensed uppercase text-[9px] tracking-[0.2em] font-bold text-white/70 bg-white/[0.06] px-2 py-1">
                      {t}
                    </span>
                  ))}
                </div>
                <p className="mt-3 font-condensed uppercase text-[10px] tracking-[0.25em] text-[#2FA36B] font-bold">
                  Ver detalhes →
                </p>
              </div>
            </motion.button>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-white/50 font-body">Nenhum projeto encontrado para este filtro.</div>
        )}

        <div className="mt-14 text-center border-t border-white/10 pt-10">
          <p className="font-condensed uppercase text-xs tracking-[0.3em] text-white/50 font-bold mb-4">
            Quer um projeto assim para sua operação?
          </p>
          <a
            href="/#contato"
            data-testid="portfolio-cta"
            className="inline-flex items-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#37B87A] text-white px-8 py-5 transition-colors"
          >
            Solicitar avaliação técnica
          </a>
        </div>
      </main>

      {/* Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelected(null)}
            data-testid="portfolio-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl bg-[#0B1B2D] border border-white/10 my-8"
            >
              <button
                onClick={() => setSelected(null)}
                data-testid="portfolio-modal-close"
                aria-label="Fechar"
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#2FA36B] transition-colors text-white"
              >
                <X size={20} />
              </button>

              <div className="grid lg:grid-cols-12 gap-0">
                {/* Photos */}
                <div className="lg:col-span-7 bg-black relative">
                  <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full min-h-[300px]">
                    <img
                      src={selected.images[photoIdx]}
                      alt={`${selected.client} - foto ${photoIdx + 1}`}
                      className="w-full h-full object-contain"
                    />
                    {selected.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setPhotoIdx((i) => Math.max(i - 1, 0))}
                          disabled={photoIdx === 0}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-[#2FA36B] disabled:opacity-30 flex items-center justify-center text-white"
                          aria-label="Anterior"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <button
                          onClick={() => setPhotoIdx((i) => Math.min(i + 1, selected.images.length - 1))}
                          disabled={photoIdx === selected.images.length - 1}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-[#2FA36B] disabled:opacity-30 flex items-center justify-center text-white"
                          aria-label="Próxima"
                        >
                          <ChevronRight size={20} />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {selected.images.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setPhotoIdx(i)}
                              className={`w-2 h-2 transition-colors ${i === photoIdx ? "bg-[#2FA36B]" : "bg-white/40 hover:bg-white/70"}`}
                              aria-label={`Foto ${i + 1}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="lg:col-span-5 p-7 sm:p-9 flex flex-col">
                  <div className="flex items-center gap-3 mb-5 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 font-condensed uppercase text-[11px] tracking-[0.25em] font-bold text-[#2FA36B] border border-[#2FA36B]/50 px-2.5 py-1">
                      <Calendar size={11} /> {selected.year}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-condensed uppercase text-[11px] tracking-[0.25em] font-bold text-white/60">
                      <MapPin size={11} /> {selected.location}
                    </span>
                  </div>

                  <div className="flex items-start gap-3 mb-4">
                    <Building2 size={18} className="text-[#2FA36B] mt-1 shrink-0" />
                    <div>
                      <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-white/40 font-bold">Cliente</p>
                      <p className="font-condensed font-bold uppercase text-lg text-white tracking-tight leading-tight">{selected.client}</p>
                    </div>
                  </div>

                  <h2 className="font-condensed font-bold uppercase text-xl sm:text-2xl text-white leading-tight tracking-tight mb-4 border-l-2 border-[#2FA36B] pl-4">
                    {selected.title}
                  </h2>

                  <p className="font-body text-white/75 text-sm leading-relaxed mb-6">
                    {selected.scope}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {selected.tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 font-condensed uppercase text-[10px] tracking-[0.25em] font-bold text-white/85 bg-white/[0.06] px-3 py-1.5">
                        <CheckCircle2 size={11} className="text-[#2FA36B]" /> {t}
                      </span>
                    ))}
                  </div>

                  <a
                    href="/#contato"
                    className="mt-auto inline-flex items-center justify-center gap-2 font-condensed uppercase text-sm tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#37B87A] text-white px-6 py-4 transition-colors"
                  >
                    Solicitar projeto semelhante
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
